// frontend/api/paymentService.js - CORRECTION COMPLÈTE
import { secureApiService } from './apiService';

// 🔒 VALIDATION DES DONNÉES DE PAIEMENT
const validatePaymentData = (data) => {
  const required = ['reservationId', 'paymentMethod'];
  const missing = required.filter(field => !data[field]);
  
  if (missing.length > 0) {
    throw new Error(`Champs manquants: ${missing.join(', ')}`);
  }
  
  const sanitized = { ...data };
  
  delete sanitized.cardDetails;
  delete sanitized.cvv;
  
  return sanitized;
};

// 🔒 VALIDATION DES IDS DE PAIEMENT
const validatePaymentId = (paymentId) => {
  if (paymentId === undefined || paymentId === null || paymentId === '') {
    throw new Error('Lien de paiement invalide. Retournez à vos réservations.');
  }
  const id = String(paymentId);
  if (id === 'undefined' || id === 'null' || id.length > 50) {
    throw new Error('Lien de paiement invalide. Retournez à vos réservations.');
  }
  return id;
};

export const paymentService = {
  // ============================================
  // 💳 MÉTHODES DE PAIEMENT DISPONIBLES
  // ============================================
  getPaymentMethods: () => 
    secureApiService.get('/paiements/moyens', {}, { 
      cacheTimeout: 60000 
    }),

  // ============================================
  // 🔐 INITIALISATION PAIEMENT (PRINCIPALE)
  // ============================================
  initiatePayment: (reservationId, method, amount = null) => {
    return secureApiService.post('/paiements/initiate', {
      reservationId: String(reservationId),
      moyenPaiement: method,
      montant: amount
    }, { timeout: 30000 });
  },

  // 🟢 YengaPay a son PROPRE endpoint (prestataire distinct de CinetPay/Stripe).
  // Réponse : { success, provider:'yengapay', paymentIds[], transactionId, paymentUrl }.
  // On normalise vers { redirectUrl, paymentId } pour un usage identique côté appelant.
  initiateYengaPay: (reservationId, amount = null) => {
    return secureApiService.post('/paiements/yengapay/initiate', {
      reservationId: String(reservationId),
      currency: 'XOF',
      montant: amount
    }, { timeout: 30000 });
  },

  // ============================================
  // 📊 OBTENIR STATUT/DÉTAILS D'UN PAIEMENT
  // ============================================
  getPaymentStatus: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.get(`/paiements/${validPaymentId}/status`, {}, { 
      useCache: false 
    });
  },

  getPaymentDetails: (paymentId) => paymentService.getPaymentStatus(paymentId),

  // ============================================
  // 💳 STRIPE CHECKOUT - CORRECTION ICI !!!
  // ============================================
  createStripeCheckout: (paymentId, options = {}) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.post(`/paiements/${validPaymentId}/stripe/checkout`, {
      successPath: `/paiement/success/${validPaymentId}`,
      cancelPath: `/paiement/${validPaymentId}`,
      ...options
    });
  },

  // ============================================
  // ✅ CONFIRMATION PAIEMENT (Stripe callback)
  // ============================================
  confirmPayment: (paymentId, sessionId) => {
    const validPaymentId = validatePaymentId(paymentId);
    
    return secureApiService.post(`/paiements/${validPaymentId}/confirm`, {
      sessionId: sessionId
    });
  },

  // ============================================
  // 📧 ENVOI BILLET PAR EMAIL
  // ============================================
  sendTicketByEmail: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.post(`/paiements/${validPaymentId}/send-email`);
  },

  // ============================================
  // 📱 ENVOI BILLET PAR SMS
  // ============================================
  sendTicketBySms: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.post(`/paiements/${validPaymentId}/send-sms`);
  },

  // ============================================
  // 💬 ENVOI BILLET PAR WHATSAPP (Point 3) — envoi serveur Twilio (repli wa.me côté client).
  // ============================================
  sendTicketByWhatsapp: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.post(`/paiements/${validPaymentId}/send-whatsapp`);
  },

  // ============================================
  // 🧾 GÉNÉRATION BILLET
  // ============================================
  generateTicket: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.post(`/paiements/${validPaymentId}/generate-ticket`);
  },

  // ============================================
  // 📄 TÉLÉCHARGEMENT BILLET
  // ============================================
  downloadTicket: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    // CORRECTION BUG2: responseType doit être dans config (2e arg) pour être transmis à axios
    // Avec responseType:'blob', dlResponse.data sera un Blob (non-vide si le backend envoie du PDF)
    return secureApiService.get(
      `/paiements/${validPaymentId}/ticket`,
      { responseType: 'blob' },   // ← config axios
      { useCache: false }          // ← options cache
    );
  },

  // ============================================
  // ❌ ANNULATION PAIEMENT
  // ============================================
  cancelPayment: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.post(`/paiements/${validPaymentId}/cancel`);
  },

  // ============================================
  // 💰 CALCUL DES FRAIS
  // ============================================
  calculateFees: (montant, method) => {
    if (!montant || montant <= 0) {
      throw new Error('Montant invalide');
    }
    return secureApiService.post('/paiements/calculate-fees', { 
      montant: parseFloat(montant), 
      method 
    });
  },

  // ============================================
  // 🔄 PAIEMENT PANIER (multiple réservations)
  // ============================================
  initiateCartPayment: (reservationIds, paymentMethod, totalAmount = null) => {
    if (!Array.isArray(reservationIds) || reservationIds.length === 0) {
      throw new Error('Liste de réservations invalide');
    }

    return secureApiService.post('/paiements/cart/initiate', {
      reservationIds: reservationIds.map(id => String(id)),
      paymentMethod: paymentMethod,
      totalAmount: totalAmount
    });
  },

  // ============================================
  // 🛒 TOUTES LES RÉSERVATIONS D'UN PANIER
  // ============================================
  getCartReservations: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    return secureApiService.get(`/paiements/${validPaymentId}/cart-reservations`, {}, { useCache: false });
  },

  // ============================================
  // 📥 TOUS LES BILLETS (pour "Tout télécharger")
  // ============================================
  getAllTickets: () => {
    return secureApiService.get('/paiements/mes-billets/all', {}, { useCache: false });
  },

  // ============================================
  // 📊 HISTORIQUE DES PAIEMENTS UTILISATEUR
  // ============================================
  getUserPayments: () => {
    return secureApiService.get('/paiements/history', {}, {
      cacheTimeout: 30000
    });
  },

  // ============================================
  // 🧾 TÉLÉCHARGEMENT REÇU PDF OFFICIEL (source unique pdfService.generateReceipt)
  //    par reservationId → /paiements/pdf/invoice/:reservationId (generateInvoicePDF)
  // ============================================
  downloadReceipt: (reservationId) => {
    return secureApiService.get(
      `/paiements/pdf/invoice/${reservationId}`,
      { responseType: 'blob' },
      { useCache: false, timeout: 60000 }
    );
  },

  // ============================================
  // 🧾 TÉLÉCHARGEMENT FACTURE
  // ============================================
  downloadInvoice: (paymentId) => {
    const validPaymentId = validatePaymentId(paymentId);
    // ⚠️ responseType DOIT être dans la config axios (2e arg), PAS dans les options (3e arg,
    // cache uniquement) — sinon la facture PDF est parsée en JSON → blob corrompu (BUG 3).
    return secureApiService.get(
      `/paiements/${validPaymentId}/invoice`,
      { responseType: 'blob' },
      { useCache: false }
    );
  },

  // ============================================
  // 🎯 UTILITAIRES POUR URLS
  // ============================================
  getSuccessUrl: (paymentId) => {
    return `/paiement/success/${paymentId}`;
  },

  getCancelUrl: (paymentId) => {
    return `/paiement/${paymentId}`;
  },

  // ============================================
  // 🟠 CINETPAY - MOBILE MONEY (Orange, MTN, Wave, Moov)
  // ============================================
  initiateCinetPayPayment: (reservationId, options = {}) => {
    return secureApiService.post('/paiements/cinetpay/initiate', {
      reservationId: String(reservationId),
      ...options
    }, { timeout: 30000 });
  },

  initiateCinetPayCartPayment: (reservationIds, options = {}) => {
    if (!Array.isArray(reservationIds) || !reservationIds.length) {
      throw new Error('Liste de réservations invalide');
    }
    return secureApiService.post('/paiements/cinetpay/initiate', {
      reservationIds: reservationIds.map(String),
      ...options
    }, { timeout: 30000 });
  },

  // ============================================
  // 🟢 YENGAPAY - Mobile Money + Wallet (agrégateur Afrique de l'Ouest)
  // ============================================
  initiateYengaPayPayment: (reservationId, options = {}) => {
    return secureApiService.post('/paiements/yengapay/initiate', {
      reservationId: String(reservationId),
      ...options
    }, { timeout: 30000 });
  },

  initiateYengaPayCartPayment: (reservationIds, options = {}) => {
    if (!Array.isArray(reservationIds) || !reservationIds.length) {
      throw new Error('Liste de réservations invalide');
    }
    return secureApiService.post('/paiements/yengapay/initiate', {
      reservationIds: reservationIds.map(String),
      ...options
    }, { timeout: 30000 });
  },

  // ============================================
  // 🔄 POLLING STATUT PAYMENT INTENT
  // Utilisé après initiation CinetPay ou Stripe pour suivre le résultat webhook
  // ============================================
  getPaymentIntentStatus: (intentId) => {
    return secureApiService.get(`/paiements/intent/${intentId}/status`, {}, { useCache: false });
  },

  getPaymentIntentByTid: (tid) => {
    return secureApiService.get(`/paiements/intent/by-tid/${encodeURIComponent(tid)}`, {}, { useCache: false });
  },

  /**
   * Poll le statut d'un payment intent jusqu'à résolution ou timeout.
   * Utilise un backoff adaptatif (réseau instable Afrique).
   * @param {number} intentId
   * @param {object} options - { maxWaitMs, onStatusChange }
   * @returns {Promise<{status, ...}>}
   */
  pollPaymentIntentUntilResolved: async (intentId, options = {}) => {
    const { maxWaitMs = 10 * 60 * 1000, onStatusChange } = options;
    const TERMINAL = ['succeeded', 'failed', 'amount_mismatch', 'fraud_suspected'];
    const startTime = Date.now();

    // Intervalle adaptatif selon latence réseau
    const getInterval = (attempt) => {
      if (attempt < 10) return 3000;
      if (attempt < 20) return 6000;
      return 15000;
    };

    let attempt = 0;
    let lastStatus = null;

    while (Date.now() - startTime < maxWaitMs) {
      try {
        const res = await paymentService.getPaymentIntentStatus(intentId);
        const status = res?.data?.status;

        if (status && status !== lastStatus) {
          lastStatus = status;
          if (onStatusChange) onStatusChange(status, res.data);
        }

        if (TERMINAL.includes(status)) {
          return res.data;
        }
      } catch (_) {
        // Ignorer les erreurs réseau - réessayer
      }

      // eslint-disable-next-line no-loop-func
      await new Promise(r => setTimeout(r, getInterval(attempt)));
      attempt++;
    }

    return { status: 'timeout', message: 'Délai de vérification dépassé' };
  },

  // ============================================
  // 🔒 FONCTIONS DE SÉCURITÉ (compatibilité)
  // ============================================
  initPayment: (data) => {
    const sanitizedData = validatePaymentData(data);
    return secureApiService.post('/paiements/init', sanitizedData);
  },

  checkPaymentStatus: (paymentId) => {
    return paymentService.getPaymentStatus(paymentId);
  },

  verifyPayment: (paymentId, data) => {
    return paymentService.confirmPayment(paymentId, data?.sessionId);
  }
};

// Export par défaut pour compatibilité
export default paymentService;